/**
 * A TAF as a band: the forecast's periods laid out along the time they apply to.
 *
 * The band exists because a TAF read as a list answers "what does it say" and
 * not "what does it say **when I get there**" — which is the only question a
 * pilot asks it. So the periods are placed proportionally to their duration and
 * the flight's own two times are marked on the same axis: a TEMPO group of
 * IFR is either under the arrival tick or it is not, and that is a fact you see
 * rather than compute.
 *
 * **Two rows, not one.** NOAA's `fcsts` are not a partition: a `TEMPO` or a
 * `PROB30` group carries its own from/to that lies INSIDE the prevailing group
 * it modifies. Drawing them in one row would either overlap two fills or imply
 * the prevailing group stops — both are lies about the forecast. So the
 * prevailing groups make the band and the temporary ones a thinner strip above
 * it, which is also how they are read: "mostly this, at times that".
 *
 * Pure, and tested in `test/weather-timeline.test.ts`: the arithmetic that
 * decides where a tick lands is exactly the arithmetic that can silently put an
 * arrival in the wrong period.
 */
import type { FlightCategory, TafPeriod } from '../../wx';
import { msOf } from './model';

/**
 * How wide a segment must be before its category code is written INSIDE it.
 *
 * A fraction of the band and not a pixel count, because the same band is 300 px
 * in a phone sheet and 800 px on a desktop and no component here measures
 * itself. Below the threshold the code still reaches the pilot twice — the
 * segment's `title` and the period list under the band.
 */
export const LABEL_MIN_FRACTION = 0.14;

export interface TafSegment {
  /** index into the TAF's own period list, so the band and the list agree */
  index: number;
  from: number;
  to: number;
  /** 0…1 along the band */
  x: number;
  w: number;
  category: FlightCategory | null;
  /** the code fits inside the segment (see {@link LABEL_MIN_FRACTION}) */
  wide: boolean;
  period: TafPeriod;
}

export type MarkKind = 'etd' | 'eta';

export interface TafMark {
  kind: MarkKind;
  at: number;
  x: number;
}

export interface TafBand {
  from: number;
  to: number;
  /** the prevailing groups — the band itself */
  base: TafSegment[];
  /** TEMPO / INTER / PROB — the strip above it */
  temp: TafSegment[];
  marks: TafMark[];
}

/**
 * A period that only ever applies "at times".
 *
 * `PROB30` arrives from NOAA as change `PROB` with probability 30, and
 * `PROB30 TEMPO` as change `TEMPO` with probability 30 — both are temporary. A
 * `BECMG` is not: it is the forecast changing for good, and it belongs on the
 * band with the groups either side of it.
 */
export const isTemporary = (p: TafPeriod): boolean =>
  p.probability !== null || p.change === 'TEMPO' || p.change === 'INTER' || p.change === 'PROB';

interface Span {
  index: number;
  from: number | null;
  to: number | null;
  period: TafPeriod;
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/**
 * The band, or null when there is nothing to draw.
 *
 * A TAF whose periods carry only one end each still draws: the missing end takes
 * the window's, which is what the forecast means (a group runs until the next
 * one starts, and the last one until the TAF expires).
 */
export function tafBand(
  periods: readonly TafPeriod[],
  marks: { etd?: number | null | undefined; eta?: number | null | undefined } = {},
): TafBand | null {
  const spans: Span[] = periods.map((p, index) => ({
    index,
    from: msOf(p.from),
    to: msOf(p.to),
    period: p,
  }));
  const known = spans.flatMap(s => [s.from, s.to]).filter((n): n is number => n !== null);
  if (known.length === 0) return null;
  const from = Math.min(...known);
  const to = Math.max(...known);
  const span = to - from;
  if (span <= 0) return null;

  const base: TafSegment[] = [];
  const temp: TafSegment[] = [];
  for (const s of spans) {
    // a group with neither end says nothing about WHEN, and a full-width bar is
    // the loudest possible way to say nothing
    if (s.from === null && s.to === null) continue;
    const a = Math.max(from, s.from ?? from);
    const b = Math.min(to, s.to ?? to);
    if (b <= a) continue;
    const w = (b - a) / span;
    const seg: TafSegment = {
      index: s.index,
      from: a,
      to: b,
      x: clamp01((a - from) / span),
      w: clamp01(w),
      category: s.period.category,
      wide: w >= LABEL_MIN_FRACTION,
      period: s.period,
    };
    (isTemporary(s.period) ? temp : base).push(seg);
  }
  if (base.length === 0 && temp.length === 0) return null;

  const out: TafMark[] = [];
  const add = (kind: MarkKind, at: number | null | undefined): void => {
    if (at === null || at === undefined || !Number.isFinite(at)) return;
    if (at < from || at > to) return;
    out.push({ kind, at, x: clamp01((at - from) / span) });
  };
  add('etd', marks.etd);
  add('eta', marks.eta);

  return { from, to, base, temp, marks: out };
}

/**
 * The category the forecast gives one instant — the prevailing group it falls
 * in, downgraded by any temporary group that covers it.
 *
 * "Downgraded" and never upgraded: a TEMPO that is better than the prevailing
 * weather changes nothing a pilot plans around, and one that is worse changes
 * everything. Used for the arrival tick's tooltip.
 */
export function categoryAt(band: TafBand | null, at: number | null | undefined): FlightCategory | null {
  if (!band || at === null || at === undefined) return null;
  const RANK: Record<FlightCategory, number> = { VFR: 0, MVFR: 1, IFR: 2, LIFR: 3 };
  let worst: FlightCategory | null = null;
  for (const s of [...band.base, ...band.temp]) {
    if (at < s.from || at > s.to || s.category === null) continue;
    if (worst === null || RANK[s.category] > RANK[worst]) worst = s.category;
  }
  return worst;
}
